import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { BusinessByBadge, GroupedBusinessResponse } from '@communitly/shared';

interface BusinessByBadgeParams {
  badgeSlug: string;
  communityId: string;
  limit?: number;
  enabled?: boolean;
  groupByCategory?: boolean;
}

// Overloaded hook signatures for type safety
export function useBusinessesByBadge(params: BusinessByBadgeParams & { groupByCategory: true }): ReturnType<typeof useQuery<GroupedBusinessResponse>>;
export function useBusinessesByBadge(params: BusinessByBadgeParams & { groupByCategory?: false }): ReturnType<typeof useQuery<BusinessByBadge[]>>;
export function useBusinessesByBadge(params: BusinessByBadgeParams): ReturnType<typeof useQuery<BusinessByBadge[]>>;
export function useBusinessesByBadge({ 
  badgeSlug, 
  communityId, 
  limit = 10,
  enabled = true,
  groupByCategory = false
}: BusinessByBadgeParams) {
  return useQuery({
    queryKey: ['businessesByBadge', badgeSlug, communityId, limit, groupByCategory],
    queryFn: async () => {
      const { data, error } = await supabase
        .rpc('get_businesses_by_badge', {
          p_badge_slug: badgeSlug,
          p_community_id: communityId,
          p_limit: limit,
          p_group_by_category: groupByCategory
        });

      if (error) {
        console.error('Error fetching businesses by badge:', error);
        throw error;
      }

      if (groupByCategory) {
        // Return grouped response as-is
        return data || {};
      } else {
        // Extract businesses from default group for backwards compatibility
        const groupedData = data || {};
        const defaultGroup = groupedData['default'];
        return defaultGroup?.businesses || [];
      }
    },
    enabled: enabled && !!badgeSlug && !!communityId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime in v4)
  });
}